import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Instagram, Facebook, Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { BUSINESS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const programOptions = [
  "Premium Mobile Swim Lessons",
  "Agua Flow Swim Academy — Group Lessons",
  "Private Lifeguard Services",
  "Not sure yet",
];

const ageOptions = [
  "6 months – 2 years",
  "2 – 5 years",
  "6 – 9 years",
  "10 – 13 years",
  "Teen",
  "Adult",
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    child_age: "",
    program: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.program) {
      toast.error("Please fill out name, email, phone, and program.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSuccess(true);
      toast.success("Thanks! We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", child_age: "", program: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(typeof msg === "string" ? msg : "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="aqua-section pb-0">
        <div className="aqua-container max-w-3xl">
          <p className="text-eyebrow">Contact / Book</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold mt-3 text-[#0B2F6B] leading-tight">
            Let's get you in the water.
          </h1>
          <p className="mt-5 text-[#3B5878] text-base sm:text-lg leading-relaxed">
            Send us a quick note and we'll be in touch within 1 business day with
            availability and pricing. Prefer to talk? Give us a call any time.
          </p>
        </div>
      </section>

      <section className="aqua-section">
        <div className="aqua-container grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <motion.form
              onSubmit={submit}
              noValidate
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="card-soft p-6 sm:p-8 space-y-5"
              data-testid="inquiry-form"
            >
              {success && (
                <div className="rounded-2xl bg-[#EAF3FF] border border-[#1E7BE0]/20 p-4 flex items-start gap-3" data-testid="form-success">
                  <CheckCircle2 className="w-5 h-5 text-[#1E7BE0] mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#0B2F6B]">Inquiry sent!</p>
                    <p className="text-sm text-[#3B5878]">Coach Félix will reach out shortly. Check your email for confirmation.</p>
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Name" required>
                  <Input
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Jane Smith"
                    data-testid="form-input-name"
                    required
                  />
                </Field>
                <Field label="Email" required>
                  <Input
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@email.com"
                    data-testid="form-input-email"
                    required
                  />
                </Field>
                <Field label="Phone" required>
                  <Input
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(910) 555-0123"
                    data-testid="form-input-phone"
                    required
                  />
                </Field>
                <Field label="Child's Age">
                  <Select value={form.child_age} onValueChange={update("child_age")}>
                    <SelectTrigger data-testid="form-select-age">
                      <SelectValue placeholder="Select age range" />
                    </SelectTrigger>
                    <SelectContent>
                      {ageOptions.map((a) => (
                        <SelectItem key={a} value={a} data-testid={`form-age-option-${a}`}>{a}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label="Program of Interest" required>
                    <Select value={form.program} onValueChange={update("program")}>
                      <SelectTrigger data-testid="form-select-program">
                        <SelectValue placeholder="Choose a program" />
                      </SelectTrigger>
                      <SelectContent>
                        {programOptions.map((p) => (
                          <SelectItem key={p} value={p} data-testid={`form-program-option-${p}`}>{p}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="Message">
                    <Textarea
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us a bit about your goals, prior swim experience, preferred location, etc."
                      data-testid="form-input-message"
                    />
                  </Field>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2">
                <p className="text-xs text-[#7C93AD]">
                  We'll only use your info to respond to your inquiry. No spam, ever.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="form-submit"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                    </>
                  ) : (
                    "Send Inquiry"
                  )}
                </button>
              </div>
            </motion.form>
          </div>

          <aside className="lg:col-span-5 space-y-4">
            <a href={BUSINESS.phoneHref} className="card-soft p-6 flex items-start gap-4 group" data-testid="contact-side-phone">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#EAF3FF" }}>
                <Phone className="w-5 h-5 text-[#1E7BE0]" />
              </span>
              <div>
                <p className="text-eyebrow">Call</p>
                <p className="font-heading font-semibold text-lg text-[#0B2F6B] group-hover:text-[#1E7BE0]">{BUSINESS.phone}</p>
                <p className="text-sm text-[#3B5878] mt-1">Tap to call from mobile</p>
              </div>
            </a>
            <a href={BUSINESS.emailHref} className="card-soft p-6 flex items-start gap-4 group" data-testid="contact-side-email">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#EAF3FF" }}>
                <Mail className="w-5 h-5 text-[#1E7BE0]" />
              </span>
              <div>
                <p className="text-eyebrow">Email</p>
                <p className="font-heading font-semibold text-lg text-[#0B2F6B] group-hover:text-[#1E7BE0] break-all">{BUSINESS.email}</p>
              </div>
            </a>
            <div className="card-soft p-6 flex items-start gap-4">
              <span className="w-11 h-11 rounded-2xl flex items-center justify-center" style={{ backgroundColor: "#EAF3FF" }}>
                <MapPin className="w-5 h-5 text-[#1E7BE0]" />
              </span>
              <div>
                <p className="text-eyebrow">Service Area</p>
                <p className="font-heading font-semibold text-lg text-[#0B2F6B]">{BUSINESS.serviceAreaShort}</p>
                <p className="text-sm text-[#3B5878] mt-1">
                  {BUSINESS.serviceAreas.slice(0, -1).join(", ")} {BUSINESS.serviceAreas.at(-1)}
                </p>
              </div>
            </div>
            <div className="card-soft p-6">
              <p className="text-eyebrow">Follow Along</p>
              <div className="mt-3 flex items-center gap-3">
                <a
                  href={BUSINESS.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#DCE7F5] hover:border-[#1E7BE0] hover:text-[#1E7BE0] text-sm"
                  data-testid="contact-instagram"
                >
                  <Instagram className="w-4 h-4" /> Instagram
                </a>
                <a
                  href={BUSINESS.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#DCE7F5] hover:border-[#1E7BE0] hover:text-[#1E7BE0] text-sm"
                  data-testid="contact-facebook"
                >
                  <Facebook className="w-4 h-4" /> Facebook
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium text-[#0B2F6B]">
        {label}
        {required && <span className="text-[#1E7BE0]"> *</span>}
      </Label>
      {children}
    </div>
  );
}
